<template>
    <div class="customer-search position-relative">

        <label v-if="label" :for="resolvedInputId" class="mb-1">{{ label }}</label>

        <input
            :id="resolvedInputId"
            ref="input"
            class="form-control"
            type="text"
            role="combobox"
            autocomplete="off"
            aria-autocomplete="list"
            :aria-label="accessibleName"
            :aria-expanded="isOpen ? 'true' : 'false'"
            :aria-controls="listboxId"
            :aria-activedescendant="isOpen && activeIndex >= 0 ? optionId(activeIndex) : null"
            :placeholder="placeholder"
            :disabled="disabled || forbidden"
            v-model="query"
            @input="onInput"
            @keydown.down.prevent="onArrow(1)"
            @keydown.up.prevent="onArrow(-1)"
            @keydown.home="onEdge($event, 'first')"
            @keydown.end="onEdge($event, 'last')"
            @keydown.enter="onEnter"
            @keydown.esc="close"
            @focus="onFocus"
            @blur="close"
        />

        <!-- read out by screen readers as the search runs -->
        <div class="sr-only" aria-live="polite" aria-atomic="true">{{ liveMessage }}</div>

        <ul
            v-show="isOpen"
            :id="listboxId"
            class="dropdown-menu show w-100 customer-search-list"
            role="listbox"
            aria-label="Members found"
        >
            <li v-if="loading" class="dropdown-item-text text-muted" role="presentation">
                <small>Searching...</small>
            </li>

            <li v-else-if="!results.length" class="dropdown-item-text text-muted" role="presentation">
                <small>No members found</small>
            </li>

            <template v-else>
            <li
                v-for="(item, index) in results"
                :key="item.customer_id"
                :id="optionId(index)"
                class="dropdown-item customer-search-option"
                :class="{ active: index === activeIndex }"
                role="option"
                :aria-selected="index === activeIndex ? 'true' : 'false'"
                @mousedown.prevent="select(item)"
                @mousemove="activeIndex = index"
            >
                <div class="d-flex justify-content-between align-items-center">
                    <span>
                        <strong>{{ item.display_name || "(no name)" }}</strong>
                        <span class="text-muted ml-1">{{ item.customer_no }}</span>
                    </span>
                    <span class="badge" :class="kycBadge(item.kyc_status_id).css">
                        {{ kycBadge(item.kyc_status_id).label }}
                    </span>
                </div>
                <small class="text-muted d-block">
                    <span v-if="item.phone_masked">{{ item.phone_masked }}</span>
                    <span v-if="item.id_no_masked"> &middot; ID {{ item.id_no_masked }}</span>
                    <span v-if="item.branch_name"> &middot; {{ item.branch_name }}</span>
                </small>
            </li>
            </template>
        </ul>

        <small v-if="message" class="text-danger d-block">{{ message }}</small>
        <small v-else-if="hint" class="text-muted d-block">{{ hint }}</small>

    </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import _ from "lodash";
import { statusEnum } from "@/enums";

const applications_module = "customerapplications";

// contract §H.1: nothing is sent below this length
const MIN_QUERY_LENGTH = 3;

let instanceCount = 0;

function labelOf(customer) {
    if (!customer) {
        return "";
    }
    return [customer.customer_no, customer.display_name].filter(Boolean).join(" - ");
}

export default defineComponent({
    name: "CustomerSearch",

    props: {
        // the selected customer {customer_id, customer_uuid, customer_no, display_name, phone_masked}, or null
        modelValue: {
            type: Object,
            default: null,
        },
        limit: {
            type: Number,
            default: 10,
        },
        statusId: {
            type: Number,
            default: null,
        },
        isGroup: {
            type: Boolean,
            default: null,
        },
        placeholder: {
            type: String,
            default: "Search by member no., phone, ID or name",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        // a visible label, tied to the input
        label: {
            type: String,
            default: "",
        },
        // the accessible name when there is no visible label
        ariaLabel: {
            type: String,
            default: "",
        },
        // the input's id, e.g. for a label outside the component
        inputId: {
            type: String,
            default: "",
        },
    },

    emits: ["update:modelValue", "select"],

    setup(props, { emit }) {
        const store = useStore();

        instanceCount += 1;
        const uid = `customer-search-${instanceCount}`;
        const resolvedInputId = computed(() => props.inputId || `${uid}-input`);
        const listboxId = `${uid}-listbox`;

        // a visible label names the input; otherwise aria-label does
        const accessibleName = computed(() => {
            if (props.label) {
                return null;
            }
            return props.ariaLabel || "Search members";
        });

        const query = ref(labelOf(props.modelValue));
        const results = ref([]);
        const open = ref(false);
        const loading = ref(false);
        const activeIndex = ref(-1);
        const message = ref("");
        const forbidden = ref(false);

        // drops a response that arrives after a newer request was sent
        let requestSeq = 0;

        // true while this component itself clears the selection because the
        // user edited the text, so the watcher keeps what they typed
        let clearingFromInput = false;

        const isOpen = computed(() => open.value && (loading.value || query.value.trim().length >= MIN_QUERY_LENGTH));

        // what the aria-live region announces: searching, the outcome, or the error
        const liveMessage = computed(() => {
            if (loading.value) {
                return "Searching…";
            }
            if (message.value) {
                return message.value;
            }
            if (isOpen.value && !results.value.length) {
                return "No members found";
            }
            if (isOpen.value) {
                return `${results.value.length} member${results.value.length === 1 ? "" : "s"} found`;
            }
            return "";
        });

        const hint = computed(() => {
            const length = query.value.trim().length;
            if (!props.modelValue && length > 0 && length < MIN_QUERY_LENGTH) {
                return `Type at least ${MIN_QUERY_LENGTH} characters`;
            }
            return "";
        });

        watch(
            () => props.modelValue,
            (value) => {
                if (value) {
                    query.value = labelOf(value);
                } else if (!clearingFromInput) {
                    // the parent reset the selection
                    query.value = "";
                    results.value = [];
                    open.value = false;
                }
                clearingFromInput = false;
            }
        );

        function optionId(index) {
            return `${uid}-option-${index}`;
        }

        function kycBadge(kycStatusId) {
            if (kycStatusId === null || kycStatusId === undefined) {
                return { label: "KYC not reviewed", css: "badge-light" };
            }
            if (kycStatusId === statusEnum.KYCVERIFIED) {
                return { label: "KYC verified", css: "badge-success" };
            }
            if (kycStatusId === statusEnum.KYCREJECTED) {
                return { label: "KYC rejected", css: "badge-danger" };
            }
            if (kycStatusId === statusEnum.KYCSUSPENDED) {
                return { label: "KYC suspended", css: "badge-warning" };
            }
            if (kycStatusId === statusEnum.KYCUNVERIFIED) {
                return { label: "KYC unverified", css: "badge-secondary" };
            }
            return { label: `KYC ${kycStatusId}`, css: "badge-secondary" };
        }

        function describeError(err) {
            const status = err?.status ?? err?.data?.status_code;
            if (status === 403) {
                forbidden.value = true;
                return "You do not have permission to search members.";
            }
            if (status === 429) {
                return "Too many searches. Wait a moment and try again.";
            }
            if (status === 422) {
                const errors = err?.data?.data;
                const first = errors && typeof errors === "object" ? Object.values(errors)[0] : null;
                return Array.isArray(first) ? first[0] : "The search could not be run.";
            }
            return "The search could not be run.";
        }

        async function runSearch(term) {
            const seq = ++requestSeq;
            const params = { q: term, limit: props.limit };

            if (props.statusId !== null && props.statusId !== undefined) {
                params.status_id = props.statusId;
            }
            if (props.isGroup !== null && props.isGroup !== undefined) {
                params.is_group = props.isGroup ? 1 : 0;
            }

            loading.value = true;
            message.value = "";

            try {
                const res = await store.dispatch(`${applications_module}/searchCustomers`, params);
                if (seq !== requestSeq) {
                    return;
                }
                results.value = Array.isArray(res?.data) ? res.data : [];
                activeIndex.value = results.value.length ? 0 : -1;
            } catch (err) {
                if (seq !== requestSeq) {
                    return;
                }
                results.value = [];
                activeIndex.value = -1;
                message.value = describeError(err);
            } finally {
                if (seq === requestSeq) {
                    loading.value = false;
                }
            }
        }

        const debouncedSearch = _.debounce((term) => {
            runSearch(term);
        }, 300);

        function onInput() {
            // editing the text drops the previous selection
            if (props.modelValue) {
                clearingFromInput = true;
                emit("update:modelValue", null);
            }

            const term = query.value.trim();
            message.value = "";

            if (term.length < MIN_QUERY_LENGTH) {
                debouncedSearch.cancel();
                requestSeq += 1;
                results.value = [];
                activeIndex.value = -1;
                loading.value = false;
                open.value = false;
                return;
            }

            open.value = true;
            debouncedSearch(term);
        }

        function onFocus() {
            if (!props.modelValue && query.value.trim().length >= MIN_QUERY_LENGTH && results.value.length) {
                open.value = true;
            }
        }

        function onArrow(step) {
            if (!open.value) {
                onFocus();
                return;
            }
            const count = results.value.length;
            if (!count) {
                return;
            }
            activeIndex.value = (activeIndex.value + step + count) % count;
        }

        // Home and End move to the first and last member while the list is
        // open; otherwise they keep moving the caret in the text
        function onEdge(event, edge) {
            const count = results.value.length;
            if (!isOpen.value || !count) {
                return;
            }
            event.preventDefault();
            activeIndex.value = edge === "first" ? 0 : count - 1;
        }

        function onEnter(event) {
            if (!isOpen.value) {
                return;
            }
            // Enter picks the highlighted row instead of submitting the form
            event.preventDefault();
            if (activeIndex.value >= 0 && results.value[activeIndex.value]) {
                select(results.value[activeIndex.value]);
            }
        }

        function close() {
            open.value = false;
        }

        function select(item) {
            const chosen = {
                customer_id: item.customer_id,
                customer_uuid: item.customer_uuid,
                customer_no: item.customer_no,
                display_name: item.display_name,
                phone_masked: item.phone_masked || null,
            };
            query.value = labelOf(chosen);
            open.value = false;
            emit("update:modelValue", chosen);
            // the whole search result (K.7) as well, for callers that show more of it
            emit("select", chosen, item);
        }

        onBeforeUnmount(() => {
            debouncedSearch.cancel();
        });

        return {
            resolvedInputId,
            accessibleName,
            listboxId,
            query,
            results,
            loading,
            activeIndex,
            message,
            liveMessage,
            hint,
            forbidden,
            isOpen,
            optionId,
            kycBadge,
            onInput,
            onFocus,
            onArrow,
            onEdge,
            onEnter,
            close,
            select,
        };
    },
});
</script>

<style scoped>
.customer-search-list {
    position: absolute;
    z-index: 1050;
    max-height: 260px;
    overflow-y: auto;
    padding: 0;
    margin: 2px 0 0;
    list-style: none;
}

.customer-search-option {
    cursor: pointer;
    white-space: normal;
}
</style>
